import React, { useState, useMemo } from 'react';
import { LOGO_BASE64 } from '../data/logo';
import { syncedGet, syncedSet, syncAllFromServer } from '../data/serverStore';

const fmt = n => `$${(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const today = () => new Date().toISOString().split('T')[0];
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate()+n); return dt.toISOString().split('T')[0]; };
const fmtDate = d => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'2-digit',day:'2-digit',year:'2-digit'}) : '';

// ── Data helpers ──────────────────────────────────────────────────────────────
function getQuotes()       { return syncedGet('sl_quotes', []); }
function saveQuotes(arr)   { return syncedSet('sl_quotes', arr); }
function nextQuoteNum()    {
  const quotes = getQuotes();
  const nums = quotes.map(q=>parseInt((q.num||'').replace(/\D/g,''))||0);
  const max = nums.length ? Math.max(...nums) : 1899;
  return `26-${max+1}`;
}

// ── Deliberately paginated quote document ────────────────────────────────────
// Keep at most five items on any page that also contains the customer details
// or totals. Multi-page quotes are balanced: 6 lines become 3 + 3, while 9
// lines become 5 + 4. This avoids browser-dependent table overflow.
function splitQuoteLines(lines) {
  const safeLines = lines || [];
  if (safeLines.length <= 5) return [safeLines];
  const pageCount = Math.ceil(safeLines.length / 5);
  const baseSize = Math.floor(safeLines.length / pageCount);
  const extra = safeLines.length % pageCount;
  const pages = [];
  let offset = 0;
  for (let i = 0; i < pageCount; i += 1) {
    const size = baseSize + (i < extra ? 1 : 0);
    pages.push(safeLines.slice(offset, offset + size));
    offset += size;
  }
  return pages;
}

function buildPaginatedQuoteHTML(q, title) {
  const totalPerSample = (q.lines||[]).reduce((a,l)=>a+(parseFloat(l.cost)||0),0);
  const numSamples = parseInt(q.numSamples)||1;
  const discount = parseFloat(q.discount)||0;
  const rush = parseFloat(q.rush)||0;
  const grandTotal = (totalPerSample + rush) * numSamples - discount;
  const pages = splitQuoteLines(q.lines || []);

  const renderRows = lines => lines.map(l=>`
    <tr class="quote-line">
      <td>${l.analysis||'&nbsp;'}</td>
      <td class="method">${l.method||'&nbsp;'}</td>
      <td class="cost">${l.cost?fmt(parseFloat(l.cost)):''}</td>
    </tr>`).join('');

  const letterhead = compact => `
    <table class="letterhead ${compact?'letterhead-compact':''}"><tr>
      <td class="logo-cell"><img src="${LOGO_BASE64}" /></td>
      <td class="address-cell">
        15 Agawam Ave<br>West Springfield, MA 01089<br>Phone: 413-214-6541<br>Fax: 413-214-6842<br>email: customerservice@sterlinganalytical.com
      </td>
    </tr></table>
    <hr class="header-rule">`;

  const analysisTable = lines => `
    <table class="quote-lines">
      <thead><tr>
        <th>Analysis Requested</th>
        <th>Method #</th>
        <th>Cost/sample</th>
      </tr></thead>
      <tbody>${renderRows(lines)}</tbody>
    </table>`;

  const totals = `
    <div class="closing-block">
      <table class="totals">
        <tr><td>Total cost/sample</td><td>${fmt(totalPerSample)}</td></tr>
        <tr><td>Turnaround time</td><td class="center">${q.turnaround||'5-7 Business Days'}</td></tr>
        <tr><td>Rush Surcharge (if applicable)</td><td>${rush?fmt(rush):'N/A'}</td></tr>
        <tr><td>Discount (if applicable)</td><td>${discount?fmt(discount):'N/A'}</td></tr>
        <tr class="grand-total"><td>Total Analysis Quote</td><td>${fmt(grandTotal)}</td></tr>
      </table>
      <div class="thank-you">**If you have any questions, please call or email us. Thank You!</div>
    </div>`;

  const renderedPages = pages.map((lines, pageIndex) => {
    const firstPage = pageIndex === 0;
    const lastPage = pageIndex === pages.length - 1;
    return `
      <section class="quote-page ${lastPage?'last-page':''}">
        ${letterhead(!firstPage)}
        ${firstPage ? `
          <table class="title-row"><tr>
            <td><div class="quote-title">Analysis Price Quote</div></td>
            <td class="quote-meta">
              <strong>Generated on &nbsp;</strong><span>${fmtDate(q.date)}</span>
              &nbsp;&nbsp;&nbsp;<strong>Quote # &nbsp;</strong><span class="quote-number">${q.num}</span>
            </td>
          </tr></table>
          <table class="contact-fields">
            <tr><td>Contact Name</td><td><div>${q.contactName||''}</div></td><td>Phone #</td><td><div>${q.phone||''}</div></td></tr>
            <tr class="spacer"><td colspan="4"></td></tr>
            <tr><td>Company</td><td><div>${q.company||''}</div></td><td>Email</td><td><div>${q.email||''}</div></td></tr>
            <tr class="spacer"><td colspan="4"></td></tr>
            <tr><td>Project Name<br><small>(if applicable)</small></td><td colspan="3"><div>${q.projectName||''}</div></td></tr>
            <tr class="spacer"><td colspan="4"></td></tr>
            <tr><td>Sample Matrix</td><td><div>${q.sampleMatrix||''}</div></td><td>Number of samples</td><td><div class="sample-count">${q.numSamples||''}</div></td></tr>
          </table>` : `
          <div class="continuation-title">
            <span>Analysis Price Quote — Continued</span>
            <span>Quote # ${q.num} &nbsp;·&nbsp; Page ${pageIndex + 1} of ${pages.length}</span>
          </div>`}
        ${analysisTable(lines)}
        ${lastPage ? totals : `<div class="continued-note">Continued on page ${pageIndex + 2}</div>`}
      </section>`;
  }).join('');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,sans-serif;font-size:12pt;color:#111;background:#ececec;}
    .quote-page{width:215.9mm;min-height:279.4mm;margin:8mm auto;padding:14mm 18mm;background:white;break-after:page;page-break-after:always;}
    .quote-page.last-page{break-after:auto;page-break-after:auto;}
    .letterhead{width:100%;border-collapse:collapse;margin-bottom:12pt;}
    .letterhead .logo-cell{width:45%;vertical-align:top;}
    .letterhead img{height:75pt;width:auto;}
    .letterhead .address-cell{width:55%;vertical-align:top;text-align:right;font-size:11pt;line-height:2;color:#333;}
    .letterhead-compact{margin-bottom:8pt;}
    .letterhead-compact img{height:52pt;}
    .letterhead-compact .address-cell{font-size:9pt;line-height:1.55;}
    .header-rule{border:0;border-top:2px solid #1a2744;margin-bottom:14pt;}
    .title-row{width:100%;border-collapse:collapse;margin-bottom:16pt;}
    .quote-title{font-size:16pt;font-weight:bold;font-style:italic;text-decoration:underline;}
    .quote-meta{text-align:right;white-space:nowrap;}
    .quote-meta span{border:1px solid #999;padding:4pt 10pt;}
    .quote-meta .quote-number{font-weight:bold;}
    .contact-fields{width:100%;border-collapse:collapse;margin-bottom:8pt;}
    .contact-fields td:nth-child(1){width:110pt;}
    .contact-fields td:nth-child(3){width:135pt;padding-left:10pt;white-space:nowrap;}
    .contact-fields td:nth-child(1),.contact-fields td:nth-child(3){font-weight:bold;padding-top:6pt;padding-bottom:6pt;vertical-align:middle;}
    .contact-fields td:nth-child(2){padding-right:10pt;}
    .contact-fields div{border:1px solid #999;padding:5pt 8pt;min-height:22pt;font-weight:normal;}
    .contact-fields small{font-weight:normal;font-size:10pt;}
    .contact-fields .spacer td{height:6pt;padding:0;}
    .contact-fields .sample-count{font-size:14pt;font-weight:bold;text-align:center;}
    .continuation-title{display:flex;justify-content:space-between;align-items:center;margin-bottom:12pt;font-size:11pt;font-weight:bold;color:#1a2744;}
    .continuation-title span:first-child{font-size:14pt;font-style:italic;}
    .quote-lines{width:100%;border-collapse:collapse;margin-top:12pt;table-layout:fixed;}
    .quote-lines th{background:#1a2744;color:white;padding:8pt 10pt;font-size:12pt;}
    .quote-lines th:nth-child(1){width:50%;text-align:left;}
    .quote-lines th:nth-child(2){width:30%;text-align:center;}
    .quote-lines th:nth-child(3){width:20%;text-align:right;}
    .quote-lines td{padding:8px 10px;border:1px solid #ccc;vertical-align:top;font-size:12pt;overflow-wrap:anywhere;}
    .quote-lines td.method{text-align:center;}
    .quote-lines td.cost{text-align:right;white-space:nowrap;}
    .quote-line{break-inside:avoid;page-break-inside:avoid;}
    .continued-note{text-align:right;margin-top:10pt;font-size:10pt;font-style:italic;color:#555;}
    .closing-block{break-inside:avoid;page-break-inside:avoid;}
    .totals{width:100%;border-collapse:collapse;margin-top:12pt;}
    .totals td:first-child{text-align:right;font-weight:bold;padding:5pt 8pt;}
    .totals td:last-child{width:130pt;border:1px solid #999;padding:5pt 8pt;text-align:right;}
    .totals td.center{text-align:center;}
    .totals .grand-total td:first-child{font-size:14pt;padding:8pt;}
    .totals .grand-total td:last-child{border:2px solid #1a2744;padding:6pt 8pt;font-size:15pt;font-weight:bold;color:#1a2744;}
    .thank-you{margin-top:24pt;font-weight:bold;font-size:12pt;}
    @media print{
      @page{size:Letter portrait;margin:0;}
      body{background:white;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      .quote-page{width:215.9mm;height:279.4mm;min-height:0;margin:0;padding:14mm 18mm;overflow:hidden;}
      .quote-lines tr,.quote-lines td,.quote-lines th{break-inside:avoid;page-break-inside:avoid;}
    }
  </style></head><body>${renderedPages}</body></html>`;
}

function openPaginatedQuote(q, title) {
  const win = window.open('','_blank','width=900,height=1100');
  if (!win) return;
  win.document.write(buildPaginatedQuoteHTML(q, title));
  win.document.close();
  win.onload = () => { win.focus(); win.print(); };
}

function printQuoteHTML(q) {
  openPaginatedQuote(q, `Quote ${q.num}`);
}

function downloadQuotePDF(q) {
  openPaginatedQuote(q, `Quote_${q.num}`);
}

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  Draft:    {bg:'#f0f4ff',color:'#1e3060'},
  Sent:     {bg:'#fff8e1',color:'#b45309'},
  Accepted: {bg:'#e8f5e9',color:'#1b5e20'},
  Declined: {bg:'#ffeaea',color:'#b71c1c'},
  Expired:  {bg:'#f5f5f5',color:'#555'},
  Invoiced: {bg:'#e8f0fe',color:'#1a73e8'},
};
const StatusBadge = ({status}) => {
  const c = STATUS_COLORS[status]||STATUS_COLORS.Draft;
  return <span style={{background:c.bg,color:c.color,borderRadius:10,padding:'2px 10px',fontSize:11,fontWeight:600}}>{status}</span>;
};

// ── Quote form modal ──────────────────────────────────────────────────────────
function QuoteModal({quote, onSave, onClose, toast}) {
  const isEdit = !!quote?.num;
  const [saving, setSaving] = useState(false);
  const [form, setF0] = useState(() => quote ? {...quote} : {
    num: nextQuoteNum(),
    date: today(),
    expires: addDays(today(), 30),
    contactName:'', company:'', phone:'', email:'',
    projectName:'', sampleMatrix:'', numSamples:'1',
    turnaround:'5-7 Business Days',
    rush:'', discount:'', notes:'', status:'Draft',
    lines:[{analysis:'',method:'',cost:''}],
  });
  const setF = (k,v) => setF0(p=>({...p,[k]:v}));
  const setLine = (i,k,v) => setF0(p=>{
    const lines=[...p.lines]; lines[i]={...lines[i],[k]:v}; return {...p,lines};
  });
  const addLine = () => setF0(p=>({...p,lines:[...p.lines,{analysis:'',method:'',cost:''}]}));
  const removeLine = i => setF0(p=>({...p,lines:p.lines.filter((_,j)=>j!==i)}));

  const totalPerSample = form.lines.reduce((a,l)=>a+(parseFloat(l.cost)||0),0);
  const numSamples = parseInt(form.numSamples)||1;
  const discount = parseFloat(form.discount)||0;
  const rush = parseFloat(form.rush)||0;
  const grandTotal = (totalPerSample + rush) * numSamples - discount;

  const inp = (label,key,opts={}) => (
    <div style={{display:'flex',flexDirection:'column',gap:3,flex:opts.flex||1}}>
      <label style={{fontSize:11,fontWeight:600,color:'#555',textTransform:'uppercase',letterSpacing:.4}}>{label}</label>
      <input value={form[key]||''} onChange={e=>setF(key,e.target.value)}
        placeholder={opts.placeholder||''} type={opts.type||'text'}
        style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',fontSize:12,outline:'none'}}/>
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'white',borderRadius:10,width:800,maxHeight:'92vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
        <div style={{background:'#1a2744',color:'white',padding:'14px 20px',borderRadius:'10px 10px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <h2 style={{fontSize:14,fontWeight:700,margin:0}}>{isEdit?`Edit Quote ${form.num}`:`New Quote #${form.num}`}</h2>
          <button onClick={onClose} style={{background:'none',border:'none',color:'white',fontSize:18,cursor:'pointer'}}>✕</button>
        </div>
        <div style={{padding:20}}>

          {/* Header row */}
          <div style={{display:'flex',gap:12,marginBottom:12}}>
            {inp('Quote #','num',{flex:.5})}
            {inp('Date','date',{type:'date',flex:.5})}
            {inp('Expires','expires',{type:'date',flex:.5})}
            <div style={{display:'flex',flexDirection:'column',gap:3,flex:.5}}>
              <label style={{fontSize:11,fontWeight:600,color:'#555',textTransform:'uppercase',letterSpacing:.4}}>Status</label>
              <select value={form.status} onChange={e=>setF('status',e.target.value)}
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',fontSize:12,outline:'none'}}>
                {Object.keys(STATUS_COLORS).map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Contact */}
          <div style={{display:'flex',gap:12,marginBottom:12}}>
            {inp('Contact Name','contactName')}
            {inp('Phone','phone')}
          </div>
          <div style={{display:'flex',gap:12,marginBottom:12}}>
            {inp('Company','company')}
            {inp('Email','email',{type:'email'})}
          </div>
          <div style={{display:'flex',gap:12,marginBottom:12}}>
            {inp('Project Name (if applicable)','projectName',{flex:2})}
          </div>
          <div style={{display:'flex',gap:12,marginBottom:16}}>
            {inp('Sample Matrix','sampleMatrix')}
            {inp('Number of Samples','numSamples',{flex:.4})}
            {inp('Turnaround Time','turnaround')}
          </div>

          {/* Line items */}
          <div style={{background:'#f8fafc',border:'1px solid #e0e4ec',borderRadius:6,padding:12,marginBottom:12}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <span style={{fontWeight:700,fontSize:12,color:'#1a2744'}}>Analysis Line Items</span>
              <button onClick={addLine}
                style={{background:'#1a2744',color:'white',border:'none',borderRadius:4,padding:'4px 10px',fontSize:11,cursor:'pointer'}}>
                + Add Line
              </button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'2fr 1.5fr 1fr 28px',gap:6,marginBottom:4}}>
              {['Analysis Requested','Method #','Cost/Sample',''].map(h=>(
                <div key={h} style={{fontSize:10,fontWeight:600,color:'#888',textTransform:'uppercase',letterSpacing:.4}}>{h}</div>
              ))}
            </div>
            {form.lines.map((l,i)=>(
              <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1.5fr 1fr 28px',gap:6,marginBottom:6}}>
                <textarea value={l.analysis} onChange={e=>setLine(i,'analysis',e.target.value)}
                  rows={2} placeholder="Description of analysis..."
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 7px',fontSize:12,resize:'vertical',outline:'none'}}/>
                <textarea value={l.method} onChange={e=>setLine(i,'method',e.target.value)}
                  rows={2} placeholder="e.g. EPA 200.7"
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 7px',fontSize:12,resize:'vertical',outline:'none'}}/>
                <input type="number" value={l.cost} onChange={e=>setLine(i,'cost',e.target.value)}
                  placeholder="0.00"
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 7px',fontSize:12,outline:'none'}}/>
                <button onClick={()=>removeLine(i)}
                  style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontSize:16,padding:0}}>✕</button>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            {inp('Rush Surcharge ($)','rush',{type:'number',placeholder:'0.00'})}
            {inp('Discount ($)','discount',{type:'number',placeholder:'0.00'})}
          </div>

          {/* Grand total display */}
          <div style={{background:'#1a2744',color:'white',borderRadius:6,padding:'10px 16px',
            display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div style={{fontSize:12}}>
              Total/sample: {fmt(totalPerSample)} × {numSamples} samples
              {rush?` + Rush: ${fmt(rush)}`:''}
              {discount?` − Discount: ${fmt(discount)}`:''}
            </div>
            <div style={{fontSize:16,fontWeight:700}}>Total: {fmt(grandTotal)}</div>
          </div>

          {/* Notes */}
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            <label style={{fontSize:11,fontWeight:600,color:'#555',textTransform:'uppercase',letterSpacing:.4}}>Notes / Terms</label>
            <textarea value={form.notes||''} onChange={e=>setF('notes',e.target.value)}
              rows={2} placeholder="Payment terms, special instructions..."
              style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',fontSize:12,resize:'vertical',outline:'none'}}/>
          </div>
        </div>

        <div style={{padding:'12px 20px',borderTop:'1px solid #e0e4ec',display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button disabled={saving} onClick={async()=>{
            if(!form.company){toast('error','Company is required');return;}
            if(saving)return;
            setSaving(true);
            try { await onSave(form); } finally { setSaving(false); }
          }} style={{background:'#1a2744',color:'white',border:'none',borderRadius:4,padding:'8px 22px',cursor:'pointer',fontSize:13,fontWeight:700}}>
            {saving?'Saving…':'💾 Save Quote'}
          </button>
          <button onClick={()=>printQuoteHTML(form)}
            style={{background:'#0078d4',color:'white',border:'none',borderRadius:4,padding:'8px 16px',cursor:'pointer',fontSize:13}}>
            🖨 Preview & Print
          </button>
          <button onClick={()=>downloadQuotePDF(form)}
            style={{background:'#16a34a',color:'white',border:'none',borderRadius:4,padding:'8px 16px',cursor:'pointer',fontSize:13}}>
            ⬇ Download PDF
          </button>
          <button onClick={onClose} style={{border:'1px solid #cdd3e4',background:'white',borderRadius:4,padding:'8px 16px',cursor:'pointer',fontSize:13}}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Quoting component ────────────────────────────────────────────────────
export default function Quoting({ toast }) {
  const [quotes, setQuotes] = useState(()=>getQuotes());
  const [showModal, setShowModal] = useState(false);
  const [editQuote, setEditQuote] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedQuote, setSelectedQuote] = useState(null);

  const refresh = () => { const q=getQuotes(); setQuotes(q); };

  const saveQuote = async (form) => {
    // Never mutate the server cache before syncedSet snapshots its base.
    // An in-place unshift made new quotes look unchanged to the merge API.
    const old = getQuotes();
    const idx = old.findIndex(q=>q.num===form.num);
    const all = idx>=0
      ? old.map(q=>q.num===form.num?form:q)
      : [form,...old];
    try {
      if (await saveQuotes(all) !== true) throw new Error('Server is still loading; please retry.');
      if (!await syncAllFromServer()) throw new Error('Unable to confirm the quote on the server.');
      const saved=getQuotes().find(q=>q.num===form.num);
      if (!saved || Object.keys(form).some(key=>JSON.stringify(saved[key])!==JSON.stringify(form[key]))) {
        throw new Error('Quote was not confirmed on the server; please check the list before retrying.');
      }
      setQuotes([...getQuotes()]);
      setSelectedQuote(saved);
      setShowModal(false);
      toast('success', idx>=0?`Quote ${form.num} updated`:`Quote ${form.num} created`);
    } catch(error) {
      toast('error',`Quote ${form.num} was not confirmed saved: ${error.message}`);
      // Keep the form open so the user can retry without losing their work.
    }
  };

  const deleteQuote = async (num) => {
    if(!window.confirm(`Delete Quote ${num}?`)) return;
    const all = getQuotes().filter(q=>q.num!==num);
    try {
      if(await saveQuotes(all)!==true || !await syncAllFromServer()) throw new Error('Server did not confirm the deletion.');
      if(getQuotes().some(q=>q.num===num)) throw new Error('Quote still exists on the server.');
      setQuotes([...getQuotes()]);
      if(selectedQuote?.num===num) setSelectedQuote(null);
      toast('info','Quote deleted');
    } catch(error) {
      toast('error',`Quote ${num} was not confirmed deleted: ${error.message}`);
    }
  };

  const updateStatus = async (num, status) => {
    const all = getQuotes().map(q=>q.num===num?{...q,status}:q);
    try {
      if(await saveQuotes(all)!==true || !await syncAllFromServer()) throw new Error('Server did not confirm the status.');
      const saved=getQuotes().find(q=>q.num===num);
      if(!saved || saved.status!==status) throw new Error('Quote status was not confirmed on the server.');
      setQuotes([...getQuotes()]);
      if(selectedQuote?.num===num) setSelectedQuote(saved);
      toast('success',`Quote ${num} → ${status}`);
    } catch(error) {
      toast('error',`Quote ${num} status was not confirmed: ${error.message}`);
    }
  };

  const filtered = useMemo(()=>{
    let r = quotes;
    if(filterStatus!=='All') r=r.filter(q=>q.status===filterStatus);
    if(search) r=r.filter(q=>
      (q.num||'').includes(search)||
      (q.company||'').toLowerCase().includes(search.toLowerCase())||
      (q.contactName||'').toLowerCase().includes(search.toLowerCase())||
      (q.projectName||'').toLowerCase().includes(search.toLowerCase())
    );
    return r;
  },[quotes,filterStatus,search]);

  const totalPerSample = q => (q.lines||[]).reduce((a,l)=>a+(parseFloat(l.cost)||0),0);
  const grandTotal = q => {
    const n=parseInt(q.numSamples)||1;
    return (totalPerSample(q)+(parseFloat(q.rush)||0))*n-(parseFloat(q.discount)||0);
  };

  return (
    <>
      <div style={{display:'flex',height:'calc(100vh - 90px)'}}>

        {/* LEFT PANEL */}
        <div style={{width:340,borderRight:'1px solid #e0e4ec',display:'flex',flexDirection:'column',background:'white'}}>
          {/* Toolbar */}
          <div style={{padding:'8px 10px',borderBottom:'1px solid #e0e4ec',display:'flex',gap:6,flexWrap:'wrap'}}>
            <button onClick={()=>{setEditQuote(null);setShowModal(true);}}
              style={{background:'#1a2744',color:'white',border:'none',borderRadius:4,padding:'6px 12px',
                cursor:'pointer',fontSize:12,fontWeight:700,whiteSpace:'nowrap'}}>
              + New Quote
            </button>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search quotes..."
              style={{flex:1,border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12,outline:'none',minWidth:0}}/>
          </div>

          {/* Status filter */}
          <div style={{padding:'6px 10px',borderBottom:'1px solid #e0e4ec',display:'flex',gap:4,flexWrap:'wrap'}}>
            {['All',...Object.keys(STATUS_COLORS)].map(s=>(
              <button key={s} onClick={()=>setFilterStatus(s)}
                style={{background:filterStatus===s?'#1a2744':'white',
                  color:filterStatus===s?'white':'#555',
                  border:'1px solid '+(filterStatus===s?'#1a2744':'#cdd3e4'),
                  borderRadius:4,padding:'3px 8px',cursor:'pointer',fontSize:11,fontWeight:500}}>
                {s}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div style={{padding:'6px 10px',borderBottom:'1px solid #e0e4ec',display:'flex',gap:12,fontSize:11,color:'#666'}}>
            <span><strong style={{color:'#1a2744'}}>{quotes.length}</strong> total</span>
            <span><strong style={{color:'#b45309'}}>{quotes.filter(q=>q.status==='Sent').length}</strong> sent</span>
            <span><strong style={{color:'#16a34a'}}>{quotes.filter(q=>q.status==='Accepted').length}</strong> accepted</span>
            <span><strong style={{color:'#dc2626'}}>{quotes.filter(q=>q.status==='Expired').length}</strong> expired</span>
          </div>

          {/* Quote list */}
          <div style={{overflowY:'auto',flex:1}}>
            {filtered.length===0?(
              <div style={{padding:30,textAlign:'center',color:'#aaa',fontSize:13}}>
                No quotes found
              </div>
            ):filtered.map(q=>(
              <div key={q.num}
                onClick={()=>setSelectedQuote(q)}
                style={{padding:'10px 12px',borderBottom:'1px solid #f0f2f8',cursor:'pointer',
                  background:selectedQuote?.num===q.num?'#eff6ff':'white',
                  borderLeft:selectedQuote?.num===q.num?'3px solid #2563eb':'3px solid transparent'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                  <span style={{fontWeight:700,fontSize:12,color:'#1a2744',fontFamily:'monospace'}}>#{q.num}</span>
                  <StatusBadge status={q.status}/>
                </div>
                <div style={{fontWeight:600,fontSize:12,color:'#333',marginBottom:2}}>{q.company||'—'}</div>
                <div style={{fontSize:11,color:'#666',marginBottom:3}}>{q.projectName||q.contactName||''}</div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:11,color:'#888'}}>{fmtDate(q.date)}{q.expires?` · exp ${fmtDate(q.expires)}`:''}</span>
                  <span style={{fontSize:12,fontWeight:700,color:'#1a2744'}}>{fmt(grandTotal(q))}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT PANEL — Quote Detail */}
        <div style={{flex:1,overflowY:'auto',background:'#f8fafc'}}>
          {!selectedQuote?(
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#aaa'}}>
              <div style={{fontSize:36,marginBottom:12}}>📋</div>
              <div style={{fontSize:14}}>Select a quote or create a new one</div>
            </div>
          ):(()=>{
            const q = selectedQuote;
            const tps = totalPerSample(q);
            const gt = grandTotal(q);
            const rush = parseFloat(q.rush)||0;
            const disc = parseFloat(q.discount)||0;
            return (
              <div style={{padding:20,maxWidth:760,margin:'0 auto'}}>

                {/* Header card */}
                <div style={{background:'white',border:'1px solid #e0e4ec',borderRadius:8,padding:16,marginBottom:12}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                    <div>
                      <div style={{fontSize:18,fontWeight:700,color:'#1a2744'}}>Quote #{q.num}</div>
                      <div style={{fontSize:12,color:'#666',marginTop:2}}>
                        {fmtDate(q.date)}{q.expires?` · Expires: ${fmtDate(q.expires)}`:''}
                      </div>
                    </div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                      <StatusBadge status={q.status}/>
                      <button onClick={()=>{setEditQuote(q);setShowModal(true);}}
                        style={{background:'#f0f4ff',border:'1px solid #c5d0e8',borderRadius:4,padding:'5px 10px',cursor:'pointer',fontSize:12}}>
                        ✏️ Edit
                      </button>
                      <button onClick={()=>printQuoteHTML(q)}
                        style={{background:'#0078d4',color:'white',border:'none',borderRadius:4,padding:'5px 10px',cursor:'pointer',fontSize:12}}>
                        🖨 Print
                      </button>
                      <button onClick={()=>downloadQuotePDF(q)}
                        style={{background:'#16a34a',color:'white',border:'none',borderRadius:4,padding:'5px 10px',cursor:'pointer',fontSize:12}}>
                        ⬇ Download PDF
                      </button>
                      <button onClick={()=>deleteQuote(q.num)}
                        style={{background:'white',border:'1px solid #fca5a5',color:'#dc2626',borderRadius:4,padding:'5px 10px',cursor:'pointer',fontSize:12}}>
                        🗑
                      </button>
                    </div>
                  </div>

                  {/* Status actions */}
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {q.status==='Draft'&&<button onClick={()=>updateStatus(q.num,'Sent')}
                      style={{background:'#fff8e1',border:'1px solid #fcd34d',color:'#92400e',borderRadius:4,padding:'4px 12px',cursor:'pointer',fontSize:11,fontWeight:600}}>
                      → Mark Sent
                    </button>}
                    {q.status==='Sent'&&<>
                      <button onClick={()=>updateStatus(q.num,'Accepted')}
                        style={{background:'#e8f5e9',border:'1px solid #86efac',color:'#166534',borderRadius:4,padding:'4px 12px',cursor:'pointer',fontSize:11,fontWeight:600}}>
                        ✓ Mark Accepted
                      </button>
                      <button onClick={()=>updateStatus(q.num,'Declined')}
                        style={{background:'#fef2f2',border:'1px solid #fca5a5',color:'#991b1b',borderRadius:4,padding:'4px 12px',cursor:'pointer',fontSize:11,fontWeight:600}}>
                        ✕ Mark Declined
                      </button>
                      <button onClick={()=>updateStatus(q.num,'Expired')}
                        style={{background:'#f5f5f5',border:'1px solid #d1d5db',color:'#555',borderRadius:4,padding:'4px 12px',cursor:'pointer',fontSize:11,fontWeight:600}}>
                        Expire
                      </button>
                    </>}
                    {q.status==='Accepted'&&<button onClick={()=>updateStatus(q.num,'Invoiced')}
                      style={{background:'#e8f0fe',border:'1px solid #93c5fd',color:'#1a73e8',borderRadius:4,padding:'4px 12px',cursor:'pointer',fontSize:11,fontWeight:600}}>
                        💰 Mark Invoiced
                    </button>}
                  </div>
                </div>

                {/* Client info */}
                <div style={{background:'white',border:'1px solid #e0e4ec',borderRadius:8,padding:16,marginBottom:12}}>
                  <div style={{fontWeight:700,fontSize:12,color:'#1a2744',marginBottom:10,textTransform:'uppercase',letterSpacing:.5}}>Client Information</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:12}}>
                    {[['Company',q.company],['Contact',q.contactName],['Phone',q.phone],['Email',q.email],
                      ['Project',q.projectName],['Sample Matrix',q.sampleMatrix],['# Samples',q.numSamples],['Turnaround',q.turnaround]
                    ].map(([k,v])=>v&&(
                      <div key={k} style={{display:'flex',gap:6}}>
                        <span style={{color:'#888',minWidth:90}}>{k}:</span>
                        <span style={{color:'#333',fontWeight:500}}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Line items */}
                <div style={{background:'white',border:'1px solid #e0e4ec',borderRadius:8,overflow:'hidden',marginBottom:12}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead>
                      <tr style={{background:'#1a2744',color:'white'}}>
                        <th style={{padding:'8px 12px',textAlign:'left',fontSize:11,fontWeight:600,letterSpacing:.4,textTransform:'uppercase'}}>Analysis Requested</th>
                        <th style={{padding:'8px 12px',textAlign:'left',fontSize:11,fontWeight:600,letterSpacing:.4,textTransform:'uppercase'}}>Method #</th>
                        <th style={{padding:'8px 12px',textAlign:'right',fontSize:11,fontWeight:600,letterSpacing:.4,textTransform:'uppercase'}}>Cost/Sample</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(q.lines||[]).map((l,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid #f0f2f8'}}>
                          <td style={{padding:'8px 12px',fontSize:12,color:'#333'}}>{l.analysis}</td>
                          <td style={{padding:'8px 12px',fontSize:12,color:'#555'}}>{l.method}</td>
                          <td style={{padding:'8px 12px',fontSize:12,color:'#1a2744',fontWeight:600,textAlign:'right'}}>{l.cost?fmt(parseFloat(l.cost)):''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div style={{background:'white',border:'1px solid #e0e4ec',borderRadius:8,padding:14,marginBottom:12}}>
                  {[
                    ['Total cost/sample', fmt(tps)],
                    ['× Number of samples', `${q.numSamples||1}`],
                    rush?['Rush surcharge', fmt(rush)]:null,
                    disc?['Discount', `-${fmt(disc)}`]:null,
                  ].filter(Boolean).map(([k,v])=>(
                    <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:12,color:'#555',borderBottom:'1px solid #f5f5f5'}}>
                      <span>{k}</span><span>{v}</span>
                    </div>
                  ))}
                  <div style={{display:'flex',justifyContent:'space-between',padding:'10px 0 4px',fontSize:15,fontWeight:700,color:'#1a2744'}}>
                    <span>Total Analysis Quote</span>
                    <span>{fmt(gt)}</span>
                  </div>
                </div>

                {q.notes&&(
                  <div style={{background:'white',border:'1px solid #e0e4ec',borderRadius:8,padding:14,fontSize:12,color:'#555'}}>
                    <strong>Notes:</strong> {q.notes}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {showModal&&(
        <QuoteModal
          quote={editQuote}
          onSave={saveQuote}
          onClose={()=>setShowModal(false)}
          toast={toast}/>
      )}
    </>
  );
}
